import type { ReactNode } from 'react';
import { Server } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import type { ConfigFlavor, InitVolume, QuotaRequest } from '@/api/opennebula';
import { overQuotaMsg } from './shared';

interface SummaryRailProps {
  name: string;
  selectedImageName?: string;
  selectedFlavor?: ConfigFlavor;
  flavor: string;
  volumes: InitVolume[];
  overQuota: boolean;
  remaining: QuotaRequest;
  issues: string[];
  showErrors: boolean;
  pending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

const groupLabel: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-sm)',
  fontWeight: 700,
  color: 'var(--vn-fg-muted)',
  marginBottom: 'var(--vn-space-1)',
};
const valueText: React.CSSProperties = { fontSize: 'var(--vn-font-size-md)' };

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--vn-space-3) 0',
        borderBottom: '1px solid var(--vn-border-subtle)',
      }}
    >
      <div style={groupLabel}>{label}</div>
      {children}
    </div>
  );
}

/** Sticky Instance Summary rail with live config + Cancel / Create footer. */
export function SummaryRail({
  name,
  selectedImageName,
  selectedFlavor,
  flavor,
  volumes,
  overQuota,
  remaining,
  issues,
  showErrors,
  pending,
  onCancel,
  onSubmit,
}: SummaryRailProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Instance Summary</CardTitle>
        <CardDescription>
          Review your instance configuration — CPU, memory, and storage — before launching.
        </CardDescription>
      </CardHeader>
      <CardBody>
        {overQuota && (
          <Alert tone="danger" title="Exceed Quota" style={{ marginBottom: 'var(--vn-space-3)' }}>
            Your configuration exceeds the current quota{overQuotaMsg(remaining) ? ` (needs ${overQuotaMsg(remaining)} more)` : ''}. Try a
            lower amount or contact support.
          </Alert>
        )}
        {showErrors && issues.length > 0 && (
          <Alert tone="danger" style={{ marginBottom: 'var(--vn-space-3)' }}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {issues.slice(0, 4).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Group label="Instance">
          <div style={valueText}>{name || '—'}</div>
        </Group>
        <Group label="Operating System / Selected Image">
          {selectedImageName ? (
            <div style={valueText}>{selectedImageName}</div>
          ) : (
            <div style={{ ...valueText, color: 'var(--vn-danger-text)' }}>
              An image is required. Please choose one.
            </div>
          )}
        </Group>
        <Group label="Type">
          <div style={valueText}>{flavor || '—'}</div>
          {selectedFlavor && (
            <>
              <div style={valueText}>{selectedFlavor.cores} vCPU</div>
              <div style={valueText}>{selectedFlavor.memory} GiB</div>
            </>
          )}
        </Group>
        <div style={{ paddingTop: 'var(--vn-space-3)' }}>
          <div style={groupLabel}>Volumes ({volumes.length})</div>
          {volumes.map((item, index) => (
            <div key={`${item.name}.${index}`} style={valueText}>
              {item.name} - {item.size} GiB
            </div>
          ))}
        </div>
      </CardBody>
      <CardFooter style={{ justifyContent: 'space-between' }}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" startIcon={<Server size={15} />} onClick={onSubmit} disabled={pending || overQuota}>
          Create new Instance
        </Button>
      </CardFooter>
    </Card>
  );
}
